// Copyright 2026, University of Maryland, All Rights Reserved
import { type FC } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Loader2 } from 'lucide-react'

import {
  Button,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui'
import { useServices } from '@/app-shell/services'
import { useAuth } from '@/subsystems/workspaces'
import { useAgentKnowledgeBases, useAgentTemplates } from '@/subsystems/agents'
import { useAppConfig } from '@/applications/shared'
import { RenderersBuilder } from '@/app-shell/components/renderers/RenderersBuilder'
import { AgentKnowledgeBases, AgentSettings } from './AgentsPage'

const AgentTemplateSettingsPage: FC = () => {
  const { agentId = '' } = useParams<{ agentId: string }>()
  const navigate = useNavigate()
  const services = useServices()
  const { selectedOrganizationId, selectedWorkspaceId, selectWorkspace } = useAuth()
  const orgId = selectedOrganizationId ?? ''
  const wsId = selectedWorkspaceId ?? ''
  const client = services.getClient()

  // Templates list gives us the source workspace coordinates (which app this
  // template lives in) without switching the user's selected workspace.
  const { data: templates = [], isLoading: templatesLoading } = useAgentTemplates(
    client, orgId, wsId,
  )
  const templateMeta = templates.find(t => t.agentId === agentId)

  // Fetch from the source app workspace so the cache key matches what
  // useUpdateAgent/useDeleteAgent invalidate — without this, edits would
  // succeed on the backend but the displayed agent would stay stale. The
  // source workspace also gates whether slots are shown.
  const { agents: appAgents, slotsEnabled, isLoading: agentLoading } = useAppConfig(
    client,
    templateMeta?.sourceOrgId ?? '',
    templateMeta?.sourceWsId ?? '',
  )
  const agent = appAgents.find(a => a.agentId === agentId)
  const {
    data: templateKnowledgeBases = [],
    isLoading: knowledgeBasesLoading,
  } = useAgentKnowledgeBases(
    client,
    templateMeta?.sourceOrgId ?? '',
    templateMeta?.sourceWsId ?? '',
    agentId,
    Boolean(templateMeta && agent),
  )

  if (!orgId || !wsId) {
    return (
      <div className="flex items-center justify-center h-64 text-sm text-muted-foreground">
        Select a workspace to view the agent library.
      </div>
    )
  }

  if (templatesLoading || (templateMeta && (agentLoading || knowledgeBasesLoading))) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!templateMeta || !agent) {
    return (
      <div className="container mx-auto px-4 py-8 space-y-4">
        <Button variant="outline" size="sm" onClick={() => navigate('/agent-library')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Library
        </Button>
        <p className="text-sm text-muted-foreground">
          Template "{agentId}" not found.
        </p>
      </div>
    )
  }

  // The pipeline builder edits the *selected* workspace's pipelines, so switch
  // to the template's source app workspace before navigating. The org/ws params
  // are included so Layout's URL-sync effect matches the new selection and
  // doesn't clobber the navigation (see the race note in AgentsPage).
  const handleEditPipeline = () => {
    selectWorkspace(templateMeta.sourceOrgId, templateMeta.sourceWsId)
    navigate(
      `/pipelines?agent=${agent.agentId}` +
      `&org=${templateMeta.sourceOrgId}&ws=${templateMeta.sourceWsId}`,
    )
  }

  return (
    <div className="container mx-auto px-4 py-4 space-y-4">
      <Button variant="outline" size="sm" onClick={() => navigate('/agent-library')}>
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Library
      </Button>
      <div>
        <div className="text-xs uppercase tracking-wide text-muted-foreground">
          Editing template
        </div>
        <h1 className="text-2xl font-semibold">{agent.label}</h1>
        <p className="text-sm text-muted-foreground">
          Template ID: <span className="font-mono">{agent.agentId}</span>
        </p>
      </div>
      <Tabs defaultValue="settings" className="w-full">
        <TabsList>
          <TabsTrigger value="settings">Settings</TabsTrigger>
          {templateKnowledgeBases.length > 0 && (
            <TabsTrigger value="knowledge-base">Knowledge Base</TabsTrigger>
          )}
          <TabsTrigger value="renderers">Renderers</TabsTrigger>
        </TabsList>

        <TabsContent value="settings" className="pt-2">
          <AgentSettings
            agent={agent}
            orgId={templateMeta.sourceOrgId}
            workspaceId={templateMeta.sourceWsId}
            slotsEnabled={slotsEnabled}
            hidePromote
            onEditPipeline={handleEditPipeline}
            onDeleted={() => navigate('/agent-library')}
          />
        </TabsContent>

        {templateKnowledgeBases.length > 0 && (
          <TabsContent value="knowledge-base" className="pt-2">
            <AgentKnowledgeBases
              knowledgeBases={templateKnowledgeBases}
              orgId={templateMeta.sourceOrgId}
              workspaceId={templateMeta.sourceWsId}
              isGlobalAdmin
            />
          </TabsContent>
        )}

        <TabsContent value="renderers" className="pt-2">
          <div className="rounded-lg border border-border bg-white">
            <RenderersBuilder
              agent={agent}
              organizationId={templateMeta.sourceOrgId}
              workspaceId={templateMeta.sourceWsId}
            />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  )
}

export default AgentTemplateSettingsPage
